import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, monthLabel, currentMonth } from "@/lib/format";
import { Card, PageHeader } from "@/components/Kit";

const Tip = ({ active, payload, label, cur }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A0A0A]/90 backdrop-blur-xl border border-white/10 rounded-md px-3 py-2 text-xs">
      {label && <p className="text-[#A1A1AA] mb-1">{monthLabel(label) || label}</p>}
      {payload.map((p) => (
        <p key={p.name} className="num" style={{ color: p.color || p.fill || p.payload?.color }}>
          {p.name}: {fmt(p.value, cur)}
        </p>
      ))}
    </div>
  );
};

export default function Analytics() {
  const { user } = useAuth();
  const cur = user?.baseCurrency || "USD";
  const month = currentMonth();
  const [trends, setTrends] = useState([]);
  const [expenseCat, setExpenseCat] = useState([]);
  const [incomeCat, setIncomeCat] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [t, e, i] = await Promise.all([
        api.get("/analytics/trends?months=6"),
        api.get(`/analytics/by-category?type=EXPENSE&month=${month}`),
        api.get(`/analytics/by-category?type=INCOME&month=${month}`),
      ]);
      setTrends(t.data);
      setExpenseCat(e.data);
      setIncomeCat(i.data);
    };
    load();
  }, [month]);

  const donut = (data, title, testid) => (
    <Card className="p-6">
      <h3 className="font-heading font-bold text-lg mb-6">{title}</h3>
      {data.length === 0 ? (
        <div className="h-72 flex items-center justify-center text-sm text-[#52525B]">No data yet</div>
      ) : (
        <div className="h-72" data-testid={testid}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="amount" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2} stroke="#111111" strokeWidth={2}>
                {data.map((e) => <Cell key={e.categoryId} fill={e.color} />)}
              </Pie>
              <Tooltip content={<Tip cur={cur} />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#A1A1AA" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <PageHeader title="Analytics" subtitle={`Insights converted to ${cur}`} />

      <Card className="p-6 mb-4">
        <h3 className="font-heading font-bold text-lg mb-6">6-Month Income vs Expenses</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trends} margin={{ left: -10, right: 8 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} stroke="#52525B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#52525B" fontSize={11} tickLine={false} axisLine={false} width={48} />
              <Tooltip content={<Tip cur={cur} />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#A1A1AA" }} />
              <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Bar dataKey="expense" name="Expense" fill="#F43F5E" radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {donut(expenseCat, "Expenses by Category", "expense-donut")}
        {donut(incomeCat, "Income by Source", "income-donut")}
      </div>
    </div>
  );
}
